/**
 * Adventure World Explorer — Main Entry Point
 *
 * Creates the Phaser.Game instance with:
 * - AUTO renderer (WebGL with Canvas fallback)
 * - 1280×720 base resolution
 * - Arcade Physics with gravity.y = 600
 * - FIT scale mode with CENTER_BOTH
 * - Min viewport: 320×180, Max viewport: 2560×1440
 * - DOM container: 'phaser-container'
 * - Challenge_Overlay canvas (hidden by default, z-index 1000)
 * - ChallengeOverlayBridge wired to GameStateCoordinator via IframeMotorAdapter
 *
 * Registers scenes: BootScene, MapSelectScene, CustomizationScene, GameScene, WorldCompleteScene, FinalConclusionScene
 *
 * Requirements: 1.1, 8.1, 8.5, 10.2, 4.1, 4.5
 */

import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { MapSelectScene } from './scenes/MapSelectScene';
import { CustomizationScene } from './scenes/CustomizationScene';
import { GameScene } from './scenes/GameScene';
import { FinalConclusionScene } from './scenes/FinalConclusionScene';
import { WorldCompleteScene } from './scenes/WorldCompleteScene';
import { wireChallengeOverlay, type ChallengeWiringResult } from './systems/ChallengeWiring';

// ─── Viewport Constraints ────────────────────────────────────────────────────

/** Minimum supported viewport dimensions */
export const MIN_WIDTH = 320;
export const MIN_HEIGHT = 180;

/** Maximum supported viewport dimensions */
export const MAX_WIDTH = 2560;
export const MAX_HEIGHT = 1440;

/** Base game resolution */
export const BASE_WIDTH = 1280;
export const BASE_HEIGHT = 720;

// ─── DOM Setup ───────────────────────────────────────────────────────────────

/**
 * Creates the DOM structure required for the game:
 * - A container div ('phaser-container') for the Phaser canvas
 * - A Challenge_Overlay canvas element (hidden by default, z-index 1000)
 *
 * Styles the container for full-viewport display.
 */
export function setupDOM(): { container: HTMLElement; overlayCanvas: HTMLCanvasElement } {
  // Create or find the game container
  let container = document.getElementById('phaser-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'phaser-container';
    document.body.appendChild(container);
  }

  // Style the container for full-viewport display
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.overflow = 'hidden';
  container.style.margin = '0';
  container.style.padding = '0';
  container.style.backgroundColor = '#000000';

  // Create the Challenge_Overlay canvas (hidden by default)
  let overlayCanvas = document.getElementById('challenge-overlay') as HTMLCanvasElement | null;
  if (!overlayCanvas) {
    overlayCanvas = document.createElement('canvas');
    overlayCanvas.id = 'challenge-overlay';
    container.appendChild(overlayCanvas);
  }

  // Style the overlay canvas: hidden by default, z-index 1000, full viewport
  overlayCanvas.style.position = 'absolute';
  overlayCanvas.style.top = '0';
  overlayCanvas.style.left = '0';
  overlayCanvas.style.width = '100%';
  overlayCanvas.style.height = '100%';
  overlayCanvas.style.zIndex = '1000';
  overlayCanvas.style.display = 'none';
  overlayCanvas.style.pointerEvents = 'none';

  // Ensure body has no margin/padding for full-viewport display
  document.body.style.margin = '0';
  document.body.style.padding = '0';
  document.body.style.overflow = 'hidden';

  return { container, overlayCanvas };
}

// ─── Phaser Game Configuration ───────────────────────────────────────────────

/**
 * Creates the Phaser game configuration object.
 * Exported for testing and customization purposes.
 */
export function createGameConfig(parentElement?: string | HTMLElement): Phaser.Types.Core.GameConfig {
  return {
    type: Phaser.AUTO,
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
    parent: parentElement ?? 'phaser-container',
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 600 },
        debug: false,
      },
    },
    scale: {
      mode: Phaser.Scale.EXPAND,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      min: {
        width: MIN_WIDTH,
        height: MIN_HEIGHT,
      },
      max: {
        width: MAX_WIDTH,
        height: MAX_HEIGHT,
      },
    },
    scene: [BootScene, MapSelectScene, CustomizationScene, GameScene, WorldCompleteScene, FinalConclusionScene],
    render: {
      pixelArt: false,
      antialias: true,
      antialiasGL: true,
    },
    audio: {
      disableWebAudio: false,
    },
    backgroundColor: '#000000',
  };
}

// ─── Game Initialization ─────────────────────────────────────────────────────

/**
 * Initializes and starts the Adventure World Explorer game.
 * Sets up the DOM structure, creates the Phaser.Game instance,
 * and wires the ChallengeOverlayBridge for motor integration.
 *
 * @returns The Phaser.Game instance, DOM references, and challenge wiring
 */
export function startGame(): {
  game: Phaser.Game;
  container: HTMLElement;
  overlayCanvas: HTMLCanvasElement;
  challengeWiring: ChallengeWiringResult | null;
} {
  const { container, overlayCanvas } = setupDOM();
  const config = createGameConfig(container);
  const game = new Phaser.Game(config);

  // The challenge wiring is initialized when the GameScene starts,
  // because it needs the GameStateCoordinator from wireGameSystems().
  // We expose a hook for the GameScene to call back into.
  let challengeWiring: ChallengeWiringResult | null = null;

  // Register a global reference so GameScene can wire the challenge overlay
  (window as any).__adventureWorldExplorer = {
    container,
    overlayCanvas,
    wireChallengeOverlay,
    setChallengeWiring: (wiring: ChallengeWiringResult) => {
      challengeWiring = wiring;
    },
  };

  return { game, container, overlayCanvas, challengeWiring };
}

// ─── Auto-start when loaded as main module ───────────────────────────────────

// Only auto-start if running in a browser environment (not during tests)
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Wait for DOM to be ready before initializing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      startGame();
    });
  } else {
    startGame();
  }
}
